<script setup lang="ts">
import { reactive, ref } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import router from '../router';
import { useAuthStore } from '../store/authStore';
import AuthSlideshow from './AuthSlideshow.vue';
import api from '../api';
import type { EpPropMergeType } from 'element-plus/es/utils';
import AppAlert from './ui/AppAlert.vue';
import PhoneInput from './ui/PhoneInput.vue';

interface RuleForm {
  email: string; password: string; contact_name: string;
  business_name: string; phone_no: string; accept_terms: boolean;
}

const loading     = ref(false);
const formSize    = ref<EpPropMergeType<StringConstructor, '' | 'default' | 'small' | 'large', unknown>>('default');
const formError   = ref('');
const ruleFormRef = ref<FormInstance>();
const ruleForm    = reactive<RuleForm>({
  email: '', password: '', contact_name: '', business_name: '', phone_no: '', accept_terms: false,
});
const store = useAuthStore();


const validateReadTerms = (rule: any, value: any, callback: any) => {
  if (value === false) callback(new Error('Please accept the terms and conditions'));
  else callback();
};
const validatePhone = (rule: any, value: any, callback: any) => {
  if (!value || value.length < 7) callback(new Error('Please enter your phone number'));
  else callback();
};

const rules = reactive<FormRules<RuleForm>>({
  contact_name:  [{ required: true, message: 'Please enter your name', trigger: 'blur' }],
  business_name: [{ required: true, message: 'Please enter your business name', trigger: 'blur' }],
  email:         [{ required: true, message: 'Please enter your email address', trigger: 'blur' }],
  phone_no:      [{ validator: validatePhone, trigger: 'blur' }],
  password:      [{ required: true, message: 'Please enter your password', trigger: 'blur' }],
  accept_terms:  [{ validator: validateReadTerms, trigger: 'blur' }],
});

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate((valid) => { if (valid) { loading.value = true; postData(); } });
};

const postData = async () => {
  try {
    const res = await api.post('customer/auth/signup', {
      name:         ruleForm.contact_name,
      email:        ruleForm.email,
      password:     ruleForm.password,
      company_name: ruleForm.business_name,
      phone:        ruleForm.phone_no,
    });
    loading.value = false;
    const data = res.data.data;
    store.setSession(data.token, data.user);
    router.push('/request');
  } catch (err: any) {
    loading.value = false;
    formError.value = err.response?.data?.message || 'Signup failed. Please try again.';
  }
};
</script>

<template>
  <div class="auth-page">
    <AuthSlideshow />
    <div class="auth-right">
      <div class="form-container">
        <div class="form-header">
          <h2 class="form-title">Create an account</h2>
          <p class="form-subtitle">Join businesses that trust us with their deliveries.</p>
        </div>
        <el-form ref="ruleFormRef" :model="ruleForm" :rules="rules"
          label-width="0px" class="auth-form" :size="formSize" v-loading="loading">
          <AppAlert :message="formError" />
          <div class="field-group">
            <label class="field-label">Full name</label>
            <el-form-item prop="contact_name">
              <el-input v-model="ruleForm.contact_name" placeholder="Jane Doe" autocomplete="name" />
            </el-form-item>
          </div>
          <div class="field-group">
            <label class="field-label">Business name</label>
            <el-form-item prop="business_name">
              <el-input v-model="ruleForm.business_name" placeholder="Acme Ltd." autocomplete="organization" />
            </el-form-item>
          </div>
          <div class="field-group">
            <label class="field-label">Email address</label>
            <el-form-item prop="email">
              <el-input v-model="ruleForm.email" placeholder="you@company.com" autocomplete="email" />
            </el-form-item>
          </div>
          <div class="field-group">
            <label class="field-label">Phone number</label>
            <el-form-item prop="phone_no">
              <PhoneInput v-model="ruleForm.phone_no" />
            </el-form-item>
          </div>
          <div class="field-group">
            <label class="field-label">Password</label>
            <el-form-item prop="password">
              <el-input type="password" v-model="ruleForm.password" placeholder="Create a password" autocomplete="off" show-password />
            </el-form-item>
          </div>
          <div class="field-group terms-group">
            <el-form-item prop="accept_terms">
              <el-checkbox v-model="ruleForm.accept_terms" size="large" class="terms-check">
                I have read and accepted the
                <a href="/terms" target="_blank" class="terms-link" @click.stop>terms and conditions</a>
              </el-checkbox>
            </el-form-item>
          </div>
          <el-form-item>
            <button class="submit-btn" @click="submitForm(ruleFormRef)" :disabled="loading">
              <span v-if="!loading">Create account</span><span v-else>Creating account…</span>
            </button>
          </el-form-item>
          <div class="bottom-link">
            Already have an account?
            <router-link to="/login" class="nav-link-inline">Sign in</router-link>
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');

.auth-page { display: flex; min-height: 100vh; font-family: 'DM Sans', sans-serif; }

.auth-right {
  flex: 1; min-width: 0; display: flex; align-items: center;
  justify-content: center; background: #fafafa; padding: 3rem 2rem;
}

.form-container { width: 100%; max-width: 400px; animation: fadeUp 0.4s ease both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.form-header { margin-bottom: 2.5rem; }
.form-title { font-family: 'DM Serif Display', serif; font-size: 2rem; font-weight: 400; color: #0f1c2e; margin: 0 0 0.5rem 0; letter-spacing: -0.02em; }
.form-subtitle { font-size: 0.9rem; color: #8a94a6; margin: 0; line-height: 1.6; }

.field-group { margin-bottom: 1.25rem; }
.label-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.4rem; }
.field-label { display: block; font-size: 0.82rem; font-weight: 500; color: #374151; margin-bottom: 0.4rem; letter-spacing: 0.01em; }
.forgot-link { font-size: 0.8rem; color: #0061eb; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.forgot-link:hover { opacity: 1; }

.auth-form :deep(.ep-input__wrapper),
.auth-form :deep(.el-input__wrapper) { border-radius: 8px !important; border: 1.5px solid #e5e7eb !important; box-shadow: none !important; background: white !important; transition: border-color 0.2s; }
.auth-form :deep(.ep-input__wrapper:hover),
.auth-form :deep(.el-input__wrapper:hover) { border-color: #b0bec5 !important; }
.auth-form :deep(.ep-input__wrapper.is-focus),
.auth-form :deep(.el-input__wrapper.is-focus) { border-color: #0061eb !important; }
.auth-form :deep(.ep-form-item),
.auth-form :deep(.el-form-item) { margin-bottom: 0; }
.auth-form :deep(input:-webkit-autofill),
.auth-form :deep(input:-webkit-autofill:hover),
.auth-form :deep(input:-webkit-autofill:focus) { -webkit-box-shadow: 0 0 0px 1000px white inset !important; box-shadow: 0 0 0px 1000px white inset !important; -webkit-text-fill-color: #374151 !important; transition: background-color 5000s ease-in-out 0s; }


.terms-group { margin-top: 0.5rem; }
/* Use Element Plus's native checkbox+label layout; allow the label to wrap and
   align the box to the first line so the text sits neatly beside it. */
.terms-check { height: auto; align-items: flex-start; }
.terms-check :deep(.ep-checkbox__input),
.terms-check :deep(.el-checkbox__input) { margin-top: 1px; }
.terms-check :deep(.ep-checkbox__label),
.terms-check :deep(.el-checkbox__label) { white-space: normal; line-height: 1.5; font-size: 0.84rem; color: #374151; }
.terms-link { color: #0061eb; text-decoration: underline; }

.submit-btn { width: 100%; height: 46px; background: #0061eb; color: white; border: none; border-radius: 8px; font-family: 'DM Sans', sans-serif; font-size: 0.95rem; font-weight: 500; cursor: pointer; margin-top: 0.75rem; transition: background 0.2s, transform 0.1s; }
.submit-btn:hover:not(:disabled) { background: #0050c7; }
.submit-btn:active:not(:disabled) { transform: scale(0.99); }
.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.bottom-link { text-align: center; margin-top: 1.75rem; font-size: 0.87rem; color: #8a94a6; padding-bottom: 1rem; }
.nav-link-inline { color: #0061eb; font-weight: 500; text-decoration: none; margin-left: 0.25rem; }
.nav-link-inline:hover { text-decoration: underline; }
.nav-link { font-size: 0.87rem; color: #0061eb; text-decoration: none; opacity: 0.7; transition: opacity 0.15s; }
.nav-link:hover { opacity: 1; }

.success-block { text-align: center; animation: fadeUp 0.4s ease both; }
.success-icon { width: 56px; height: 56px; background: #0061eb; color: white; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; margin: 0 auto 1.5rem; }
.success-block .form-title { margin-bottom: 0.75rem; }
.success-block .form-subtitle { margin-bottom: 2rem; }
.back-link { font-size: 0.87rem; color: #0061eb; text-decoration: none; font-weight: 500; }
.back-link:hover { text-decoration: underline; }

@media (max-width: 900px) {
  .auth-right { background: white; }
}
</style>